import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/lib/seo";

const INTRO =
  "These Terms of Use (the \"Terms\") govern your access to and use of the website at wgroupconsulting.co.uk (the \"website\"), operated by W Group Consulting. By accessing or using the website you agree to be bound by these Terms. If you do not agree with them, please do not use the website.";

const SECTIONS = [
  {
    title: "1. Website operator",
    body: "This website is operated by W Group Consulting (\"we\", \"us\" or \"our\"). You can contact us in relation to the website by email at hello@wgroupconsulting.co.uk.",
  },
  {
    title: "2. Purpose and permitted use",
    body: "The website is provided for general informational purposes only. It is intended to allow prospective clients, professional advisers and other visitors to understand the general nature of our services and to make contact with us. You may view, and print or download extracts of, the pages of the website for your own lawful, non-commercial informational use. You must not misuse the website, attempt to gain unauthorised access to it or to its underlying systems, introduce malicious code, or use the website in any way that breaches applicable law or regulation.",
  },
  {
    title: "3. General information",
    body: "The content of the website describes, in general terms, the nature of W Group Consulting and the services it offers. It is necessarily general and summary in nature and may be updated or amended from time to time.",
  },
  {
    title: "4. No client relationship",
    body: "Nothing on the website constitutes an offer to provide services, and using the website or submitting an enquiry does not create a client, advisory or professional relationship of any kind. Any engagement with W Group Consulting is formed only under a separate written agreement.",
  },
  {
    title: "5. No professional advice",
    body: "The website does not provide legal, financial, accounting, tax, investment or other professional advice, and nothing on it should be relied upon as such. The information provided is general in nature and does not take into account the circumstances of any particular person. You should obtain independent professional advice where appropriate.",
  },
  {
    title: "6. Accuracy and completeness of information",
    body: "We take reasonable care to ensure that the information on the website is accurate at the time it is published. However, the website and its content are provided \"as is\" and \"as available\", and we make no representation or warranty of any kind, express or implied, as to the accuracy, completeness, currency or fitness for any purpose of the information contained on the website.",
  },
  {
    title: "7. Website availability",
    body: "We do not warrant that the website, or any content on it, will always be available or uninterrupted, or that it will be free from errors, viruses or other harmful components. We may suspend, withdraw, discontinue or change all or any part of the website without notice. We will not be liable if, for any reason, the website is unavailable at any time or for any period.",
  },
  {
    title: "8. Intellectual property",
    body: "We are the owner or licensee of all intellectual property rights in the website and in the material published on it, including the W Group name and logo, the design and layout of the website, and its text content. Those works are protected by copyright and other intellectual property laws. Photographic imagery is used under licence from its provider and does not depict our premises or people. Except as permitted under clause 2 (Purpose and permitted use), you may not copy, reproduce, republish, modify, distribute or exploit any part of the website for commercial purposes without our prior written consent.",
  },
  {
    title: "9. Third-party links",
    body: "Where the website contains links to websites or resources provided by third parties, those links are provided for convenience only. We have no control over, and accept no responsibility for, the content, products, services or practices of any third-party website, and a link does not imply endorsement.",
  },
  {
    title: "10. Liability and disclaimers",
    body: "To the fullest extent permitted by applicable law, we exclude all representations, warranties and conditions relating to the website and its use, and we will not be liable to any visitor for any loss or damage, whether direct, indirect or consequential (including loss of profit, revenue, business, anticipated savings, goodwill or data), arising out of or in connection with the use of, the inability to use, or reliance on the website or its content, even if foreseeable. Nothing in these Terms excludes or limits any liability that cannot be excluded or limited under applicable law.",
  },
  {
    title: "11. Privacy",
    body: "Any personal information provided to us in connection with the website is handled in accordance with our Privacy Policy, which is available on the website. By using the website you acknowledge the terms of that policy.",
  },
  {
    title: "12. Changes to the website and to these Terms",
    body: "We may revise these Terms, and amend or withdraw the website or any of its content, at any time and without notice. The current version of the Terms will be published on this page. By continuing to use the website after changes are published, you accept the revised Terms.",
  },
  {
    title: "13. Applicable law and jurisdiction",
    body: "These Terms, and any dispute or claim arising out of or in connection with them or the website (including non-contractual disputes or claims), shall be governed by and construed in accordance with the laws of England and Wales, and the courts of England and Wales shall have exclusive jurisdiction.",
  },
  {
    title: "14. Contact details",
    body: "If you have any questions about these Terms or about the website, please contact us by email at hello@wgroupconsulting.co.uk.",
  },
];

export default function Terms() {
  usePageMeta(
    "Terms of Use — W Group Consulting",
    "The terms that govern use of the W Group Consulting website.",
    "/terms"
  );

  return (
    <>
      <PageHero
        eyebrow="Legal"
        titleLines={["Terms of Use"]}
        description="The terms that govern use of the W Group Consulting website."
      />
      <section className="mx-auto max-w-3xl px-6 py-24 lg:py-32" data-testid="terms-content">
        <Reveal>
          <p className="text-base leading-relaxed text-gray-600">{INTRO}</p>
        </Reveal>
        {SECTIONS.map((s) => (
          <Reveal key={s.title}>
            <h2 className="mt-12 font-display text-2xl text-ink">{s.title}</h2>
            <p className="mt-4 text-base leading-relaxed text-gray-600">{s.body}</p>
          </Reveal>
        ))}
        <p className="mt-16 border-t border-ink/10 pt-8 text-sm text-gray-400">
          Last updated: October 2026
        </p>
      </section>
    </>
  );
}
